import { formatCurrency } from 'utils/fuctions/formatCurrency';
import { parseIntFromString } from 'utils/fuctions/parseIntFromString';
import { maskCnpj, maskCpf } from '../utils/cpfCnpjHelpers';
import { toBrazilianDate } from '../utils/date';
import { keepOnlyNumericCaracters } from './stringHelpers';
import { normalizePhoneNumber } from 'utils/fuctions/internationalPhoneFormatter';
import { normalizeDDI } from 'utils/fuctions/internationalPhoneFormatter';

const {
  customFieldTypes
} = require('views/fieldConfigs/utils/customFieldTypes');
const _ = require('lodash');

/**
 * @param {Object} data
 * @param {Array} customFieldsConfig
 * @returns {Object}
 */
export const formatCustomFieldsToSubmit = (data, customFieldsConfig) => {
  if (!data) throw new Error('data is required');
  if (!customFieldsConfig) throw new Error('customFieldsConfig is required');

  const dataDTO = _.cloneDeep(data);

  const multipleCustomFields =
    customFieldsConfig &&
    customFieldsConfig
      .filter((field) => field.type === customFieldTypes.MULTIPLE)
      .map((field) => field.name);

  multipleCustomFields.forEach((fieldName) => {
    if (dataDTO[fieldName]) {
      dataDTO[fieldName] = dataDTO[fieldName].join(',');
    } else {
      dataDTO[fieldName] = null;
    }
  });

  const numericCustomFields =
    customFieldsConfig &&
    customFieldsConfig
      .filter((field) => field.type === customFieldTypes.NUMERIC)
      .map((field) => field.name);

  Object.keys(dataDTO).forEach((fieldName) => {
    if (numericCustomFields.includes(fieldName)) {
      if (dataDTO[fieldName]) {
        dataDTO[fieldName] = Number(dataDTO[fieldName]);
      } else {
        dataDTO[fieldName] = null;
      }
    }
  });

  const currencyFieldsName =
    customFieldsConfig &&
    customFieldsConfig
      .filter((field) => field.type === customFieldTypes.CURRENCY)
      .map((field) => field.name);

  currencyFieldsName.forEach((item) => {
    const fieldValue = dataDTO[item];
    if (fieldValue) {
      dataDTO[item] = parseIntFromString(fieldValue);
    } else {
      dataDTO[item] = null;
    }
  });

  const cpfFieldNames =
    customFieldsConfig &&
    customFieldsConfig
      .filter((field) => field.type === customFieldTypes.CPF)
      .map((field) => field.name);

  cpfFieldNames.forEach((item) => {
    const fieldValue = dataDTO[item];
    if (fieldValue) {
      dataDTO[item] = keepOnlyNumericCaracters(fieldValue);
    }
  });

  const cnpjFieldNames =
    customFieldsConfig &&
    customFieldsConfig
      .filter((field) => field.type === customFieldTypes.CNPJ)
      .map((field) => field.name);

  cnpjFieldNames.forEach((item) => {
    const fieldValue = dataDTO[item];
    if (fieldValue) {
      dataDTO[item] = keepOnlyNumericCaracters(fieldValue);
    }
  });

  const telephoneFieldNames =
    customFieldsConfig &&
    customFieldsConfig
      .filter((field) => field.type === customFieldTypes.TELEPHONE)
      .map((field) => field.name);

  telephoneFieldNames.forEach((item) => {
    const fieldValue = dataDTO[item];

    if (fieldValue)
      dataDTO[item] = {
        ddi: normalizeDDI(fieldValue?.ddi),
        number: normalizePhoneNumber(fieldValue?.number)
      };
  });

  return dataDTO;
};

export const formatCustomFieldsToDisplay = (data, customFieldsConfig) => {
  if (!data) throw new Error('data is required');
  if (!customFieldsConfig) throw new Error('customFieldsConfig is required');

  const dataDTO = _.cloneDeep(data);

  const multipleCustomFields =
    customFieldsConfig &&
    customFieldsConfig
      .filter((field) => field.type === customFieldTypes.MULTIPLE)
      .map((field) => field.name);

  const currencyFieldsName =
    customFieldsConfig &&
    customFieldsConfig
      .filter((field) => field.type === customFieldTypes.CURRENCY)
      .map((field) => field.name);
  const dateFieldsName =
    customFieldsConfig &&
    customFieldsConfig
      .filter((field) => field.type === customFieldTypes.DATE)
      .map((field) => field.name);
  const telephoneFieldsName =
    customFieldsConfig &&
    customFieldsConfig
      .filter((field) => field.type === customFieldTypes.TELEPHONE)
      .map((field) => field.name);
  const cpfFieldsName =
    customFieldsConfig &&
    customFieldsConfig
      .filter((field) => field.type === customFieldTypes.CPF)
      .map((field) => field.name);
  const cnpjFieldsName =
    customFieldsConfig &&
    customFieldsConfig
      .filter((field) => field.type === customFieldTypes.CNPJ)
      .map((field) => field.name);

  Object.keys(dataDTO).forEach((key) => {
    if (currencyFieldsName.includes(key) && dataDTO[key]) {
      dataDTO[key] = formatCurrency(parseInt(dataDTO[key], 10));
    } else if (dateFieldsName.includes(key) && dataDTO[key]) {
      dataDTO[key] = dataDTO[key] ? toBrazilianDate(dataDTO[key]) : '';
    } else if (telephoneFieldsName.includes(key) && dataDTO[key]) {
      dataDTO[key] = {
        ddi: normalizeDDI(dataDTO[key]?.ddi),
        number: normalizePhoneNumber(dataDTO[key]?.number)
      };
    } else if (cpfFieldsName.includes(key) && dataDTO[key]) {
      dataDTO[key] = maskCpf(dataDTO[key]);
    } else if (cnpjFieldsName.includes(key) && dataDTO[key]) {
      dataDTO[key] = maskCnpj(dataDTO[key]);
    } else if (multipleCustomFields.includes(key) && dataDTO[key]) {
      dataDTO[key] = dataDTO[key].split(',');
    }
  });
  return dataDTO;
};

/**
 * Remove os campos auxiliares criados pelo PhoneInput (_ddi e _number).
 * Se os auxiliares existirem e o campo principal não, cria o principal a partir deles.
 * Sempre remove os auxiliares se eles existirem.
 */
export const removeTelephoneAuxiliaryFields = (dataDTO, customFieldsConfig) => {
  const cloned = { ...dataDTO };

  const telephoneCustomFieldNames =
    customFieldsConfig
      ?.filter((field) => field.type === customFieldTypes.TELEPHONE)
      ?.map((field) => field.name) ?? [];

  const possiblePhoneBases = [...telephoneCustomFieldNames];
  if ('phone' in cloned) {
    possiblePhoneBases.push('phone');
  }

  for (let i = 0; i < possiblePhoneBases?.length; i++) {
    const baseName = possiblePhoneBases[i];
    const ddiKey = `${baseName}_ddi`;
    const numberKey = `${baseName}_number`;

    if (
      Object.prototype.hasOwnProperty.call(cloned, ddiKey) ||
      Object.prototype.hasOwnProperty.call(cloned, numberKey)
    ) {
      const ddi = cloned[ddiKey] || '';
      const number = cloned[numberKey] || '';

      if (
        !cloned[baseName] ||
        typeof cloned[baseName] !== 'object' ||
        !('ddi' in cloned[baseName]) ||
        !('number' in cloned[baseName])
      ) {
        cloned[baseName] = { ddi, number };
      }

      delete cloned[ddiKey];
      delete cloned[numberKey];
    }
  }

  return cloned;
};
